// timerService.ts
// Esta es una implementación de un timer global que persiste entre navegaciones

type TimerState = {
  timeLeft: number;
  isRunning: boolean;
  duration: number;
};

type TimerSubscriber = (timerState: TimerState) => void;

class TimerService {
  private timerState: TimerState;
  private subscribers: TimerSubscriber[] = [];
  private intervalId: number | null = null;

  constructor() {
    // Inicializar desde localStorage si existe
    const savedTimer = localStorage.getItem('pairTimer');
    if (savedTimer) {
      try {
        this.timerState = JSON.parse(savedTimer);
      } catch (error) {
        console.error('Failed to parse saved timer:', error);
        this.timerState = this.getDefaultTimerState();
      }
    } else {
      this.timerState = this.getDefaultTimerState();
    }

    // Iniciar el timer si estaba corriendo
    if (this.timerState.isRunning) {
      this.startTimer();
    }
  }

  private getDefaultTimerState(): TimerState {
    return {
      timeLeft: 25 * 60, // 25 minutos en segundos
      isRunning: false,
      duration: 25 // duración en minutos
    };
  }

  // Obtener el estado actual del timer
  getTimerState(): TimerState {
    return { ...this.timerState };
  }

  // Suscribirse a cambios en el timer
  subscribe(callback: TimerSubscriber): () => void {
    this.subscribers.push(callback);
    
    // Notificar inmediatamente con el estado actual
    callback(this.getTimerState());
    
    // Retornar función para desuscribirse
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  // Notificar a todos los suscriptores
  private notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.getTimerState()));
    // Guardar en localStorage
    localStorage.setItem('pairTimer', JSON.stringify(this.timerState));
  }

  // Iniciar/reanudar el timer
  startTimer() {
    if (!this.intervalId) {
      this.timerState.isRunning = true;
      this.intervalId = window.setInterval(() => {
        if (this.timerState.timeLeft > 0) {
          this.timerState.timeLeft -= 1;
          this.notifySubscribers();
        } else {
          this.pauseTimer();
        }
      }, 1000);
      this.notifySubscribers();
    }
  }

  // Pausar el timer
  pauseTimer() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
      this.timerState.isRunning = false;
      this.notifySubscribers();
    }
  }

  // Alternar entre iniciar y pausar
  toggleTimer() {
    if (this.timerState.isRunning) {
      this.pauseTimer();
    } else {
      this.startTimer();
    }
  }

  // Resetear el timer
  resetTimer() {
    this.pauseTimer();
    this.timerState.timeLeft = this.timerState.duration * 60;
    this.notifySubscribers();
  }

  // Actualizar la duración del timer
  updateTimerDuration(minutes: number) {
    this.pauseTimer();
    this.timerState.duration = minutes;
    this.timerState.timeLeft = minutes * 60;
    this.notifySubscribers();
  }
}

// Creamos una única instancia para toda la aplicación
const timerService = new TimerService();

export default timerService; 